/**
 * Incremental JSON Parser
 * Parses partial/incomplete JSON strings and extracts available data
 * Supports full hierarchy: Track → Maps → Lanes → Patterns → Cards
 */

import type { AITrackData, AIMap, AILane, AIPattern, AICard } from '@/components/ai/AITrackPreviewModal';

interface ParseResult {
  isTrackCreation: boolean;
  data: AITrackData | null;
  isComplete: boolean;
  rawJson: string | null;
}

/**
 * Detect if the streaming content contains a track creation action
 */
export function detectTrackCreation(content: string): boolean {
  return (
    content.includes('```json:action') ||
    content.includes('"action"') ||
    content.includes('"create_track"') ||
    content.includes('"trackName"') ||
    content.includes('```json')
  );
}

/**
 * Extract JSON content from streaming text
 */
export function extractJsonContent(content: string): string | null {
  // Try to find json:action block first
  let match = content.match(/```json:action\s*([\s\S]*?)(?:```|$)/);
  if (match) return match[1].trim();

  // Try regular json block
  match = content.match(/```json\s*([\s\S]*?)(?:```|$)/);
  if (match) return match[1].trim();

  // Try to find raw JSON object
  const jsonStart = content.indexOf('{');
  if (jsonStart !== -1) {
    return content.substring(jsonStart);
  }

  return null;
}

/**
 * Parse partial JSON string, handling incomplete structures
 */
export function parsePartialJson(jsonStr: string): any {
  // Try direct parse first
  try {
    return JSON.parse(jsonStr);
  } catch {
    // Continue with partial parsing
  }

  // Try to fix common incomplete patterns
  let fixed = jsonStr;

  // Count open/close brackets
  const openBraces = (fixed.match(/{/g) || []).length;
  const closeBraces = (fixed.match(/}/g) || []).length;
  const openBrackets = (fixed.match(/\[/g) || []).length;
  const closeBrackets = (fixed.match(/]/g) || []).length;

  // Remove trailing comma before closing
  fixed = fixed.replace(/,\s*$/, '');
  fixed = fixed.replace(/,\s*([}\]])/g, '$1');

  // Handle incomplete strings
  const quoteCount = (fixed.match(/"/g) || []).length;
  if (quoteCount % 2 !== 0) {
    const lastQuoteIdx = fixed.lastIndexOf('"');
    const afterLastQuote = fixed.substring(lastQuoteIdx + 1);
    if (!afterLastQuote.includes(':') && !afterLastQuote.includes('{') && !afterLastQuote.includes('[')) {
      fixed = fixed + '"';
    }
  }

  // Close missing brackets
  for (let i = 0; i < openBrackets - closeBrackets; i++) {
    fixed += ']';
  }
  for (let i = 0; i < openBraces - closeBraces; i++) {
    fixed += '}';
  }

  // Try to parse fixed JSON
  try {
    return JSON.parse(fixed);
  } catch {
    // Try more aggressive fixes
  }

  // Last resort: extract key-value pairs manually
  return extractKeyValuePairs(jsonStr);
}

/**
 * Generate a temporary ID
 */
function generateId(): string {
  return `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Extract key-value pairs manually from malformed JSON
 */
function extractKeyValuePairs(str: string): any {
  const result: any = {};

  // Extract trackType
  const trackTypeMatch = str.match(/"trackType"\s*:\s*"([^"]*)"/);
  if (trackTypeMatch) result.trackType = trackTypeMatch[1];

  // Extract trackName
  const trackNameMatch = str.match(/"trackName"\s*:\s*"([^"]*)"/);
  if (trackNameMatch) result.trackName = trackNameMatch[1];

  // Extract trackColor
  const trackColorMatch = str.match(/"trackColor"\s*:\s*"([^"]*)"/);
  if (trackColorMatch) result.trackColor = trackColorMatch[1];

  // Extract trackIcon
  const trackIconMatch = str.match(/"trackIcon"\s*:\s*"([^"]*)"/);
  if (trackIconMatch) result.trackIcon = trackIconMatch[1];

  // Extract category
  const categoryMatch = str.match(/"category"\s*:\s*"([^"]*)"/);
  if (categoryMatch) result.category = categoryMatch[1];

  // Extract maps array (for hierarchical tracks)
  const mapsMatch = str.match(/"maps"\s*:\s*\[/);
  if (mapsMatch) {
    result.maps = extractMaps(str.substring(mapsMatch.index! + mapsMatch[0].length));
  }

  // Extract patterns array (for simpler hierarchical structure)
  const patternsMatch = str.match(/"patterns"\s*:\s*\[/);
  if (patternsMatch && !mapsMatch) {
    // If we have patterns but no maps, wrap them in a default structure
    const patterns = extractPatterns(str.substring(patternsMatch.index! + patternsMatch[0].length));
    if (patterns.length > 0) {
      result.maps = [{
        id: generateId(),
        name: 'Main',
        lanes: [{
          id: generateId(),
          name: 'Default',
          patterns: patterns,
        }],
      }];
    }
  }

  // Extract direct cards array (for linear tracks)
  const cardsMatch = str.match(/"cards"\s*:\s*\[/);
  if (cardsMatch && !patternsMatch && !mapsMatch) {
    result.cards = extractCards(str.substring(cardsMatch.index! + cardsMatch[0].length));
  }

  return result;
}

/**
 * Extract maps from partial JSON
 */
function extractMaps(str: string): AIMap[] {
  const maps: AIMap[] = [];
  let depth = 1;
  let current = '';
  let inString = false;

  for (let i = 0; i < str.length && depth > 0; i++) {
    const char = str[i];
    const prevChar = str[i - 1];

    if (char === '"' && prevChar !== '\\') {
      inString = !inString;
    }

    if (!inString) {
      if (char === '{') depth++;
      else if (char === '}') {
        depth--;
        if (depth === 1) {
          current += char;
          const map = parseMapObject(current);
          if (map.name || (map.lanes && map.lanes.length > 0)) {
            maps.push(map);
          }
          current = '';
          continue;
        }
      } else if (char === '[') depth++;
      else if (char === ']') depth--;
    }

    if (depth >= 1) current += char;
  }

  // Try to parse remaining content
  if (current.trim()) {
    const map = parseMapObject(current);
    if (map.name || (map.lanes && map.lanes.length > 0)) {
      maps.push(map);
    }
  }

  return maps;
}

/**
 * Parse a single map object
 */
function parseMapObject(str: string): AIMap {
  const map: AIMap = {
    id: generateId(),
    name: '',
    lanes: [],
  };

  // Extract name
  const nameMatch = str.match(/"name"\s*:\s*"([^"]*)"/);
  if (nameMatch) map.name = nameMatch[1];

  // Extract description
  const descMatch = str.match(/"description"\s*:\s*"([^"]*)"/);
  if (descMatch) map.description = descMatch[1];

  // Extract lanes
  const lanesMatch = str.match(/"lanes"\s*:\s*\[/);
  if (lanesMatch) {
    map.lanes = extractLanes(str.substring(lanesMatch.index! + lanesMatch[0].length));
  }

  return map;
}

/**
 * Extract lanes from partial JSON
 */
function extractLanes(str: string): AILane[] {
  const lanes: AILane[] = [];
  let depth = 1;
  let current = '';
  let inString = false;

  for (let i = 0; i < str.length && depth > 0; i++) {
    const char = str[i];
    const prevChar = str[i - 1];

    if (char === '"' && prevChar !== '\\') {
      inString = !inString;
    }

    if (!inString) {
      if (char === '{') depth++;
      else if (char === '}') {
        depth--;
        if (depth === 1) {
          current += char;
          const lane = parseLaneObject(current);
          if (lane.name || (lane.patterns && lane.patterns.length > 0)) {
            lanes.push(lane);
          }
          current = '';
          continue;
        }
      } else if (char === '[') depth++;
      else if (char === ']') depth--;
    }

    if (depth >= 1) current += char;
  }

  if (current.trim()) {
    const lane = parseLaneObject(current);
    if (lane.name || (lane.patterns && lane.patterns.length > 0)) {
      lanes.push(lane);
    }
  }

  return lanes;
}

/**
 * Parse a single lane object
 */
function parseLaneObject(str: string): AILane {
  const lane: AILane = {
    id: generateId(),
    name: '',
    patterns: [],
  };

  const nameMatch = str.match(/"name"\s*:\s*"([^"]*)"/);
  if (nameMatch) lane.name = nameMatch[1];

  const descMatch = str.match(/"description"\s*:\s*"([^"]*)"/);
  if (descMatch) lane.description = descMatch[1];

  const patternsMatch = str.match(/"patterns"\s*:\s*\[/);
  if (patternsMatch) {
    lane.patterns = extractPatterns(str.substring(patternsMatch.index! + patternsMatch[0].length));
  }

  return lane;
}

/**
 * Extract patterns from partial JSON
 */
function extractPatterns(str: string): AIPattern[] {
  const patterns: AIPattern[] = [];
  let depth = 1;
  let current = '';
  let inString = false;

  for (let i = 0; i < str.length && depth > 0; i++) {
    const char = str[i];
    const prevChar = str[i - 1];

    if (char === '"' && prevChar !== '\\') {
      inString = !inString;
    }

    if (!inString) {
      if (char === '{') depth++;
      else if (char === '}') {
        depth--;
        if (depth === 1) {
          current += char;
          const pattern = parsePatternObject(current);
          if (pattern.name || pattern.cards.length > 0) {
            patterns.push(pattern);
          }
          current = '';
          continue;
        }
      } else if (char === '[') depth++;
      else if (char === ']') depth--;
    }

    if (depth >= 1) current += char;
  }

  if (current.trim()) {
    const pattern = parsePatternObject(current);
    if (pattern.name || pattern.cards.length > 0) {
      patterns.push(pattern);
    }
  }

  return patterns;
}

/**
 * Parse a single pattern object
 */
function parsePatternObject(str: string): AIPattern {
  const pattern: AIPattern = {
    id: generateId(),
    name: '',
    cards: [],
  };

  const nameMatch = str.match(/"name"\s*:\s*"([^"]*)"/);
  if (nameMatch) pattern.name = nameMatch[1];

  const descMatch = str.match(/"description"\s*:\s*"([^"]*)"/);
  if (descMatch) pattern.description = descMatch[1];

  const difficultyMatch = str.match(/"difficulty"\s*:\s*"([^"]*)"/);
  if (difficultyMatch) pattern.difficulty = difficultyMatch[1] as any;

  const priorityMatch = str.match(/"priority"\s*:\s*"([^"]*)"/);
  if (priorityMatch) pattern.priority = priorityMatch[1] as any;

  const minutesMatch = str.match(/"estimatedMinutes"\s*:\s*(\d+)/);
  if (minutesMatch) pattern.estimatedMinutes = parseInt(minutesMatch[1]);

  const cardsMatch = str.match(/"cards"\s*:\s*\[/);
  if (cardsMatch) {
    pattern.cards = extractCards(str.substring(cardsMatch.index! + cardsMatch[0].length));
  }

  return pattern;
}

/**
 * Extract cards from partial JSON
 */
function extractCards(str: string): AICard[] {
  const cards: AICard[] = [];
  let depth = 1;
  let current = '';
  let inString = false;

  for (let i = 0; i < str.length && depth > 0; i++) {
    const char = str[i];
    const prevChar = str[i - 1];

    if (char === '"' && prevChar !== '\\') {
      inString = !inString;
    }

    if (!inString) {
      if (char === '{') depth++;
      else if (char === '}') {
        depth--;
        if (depth === 1) {
          current += char;
          const card = parseCardObject(current);
          if (card.title) {
            cards.push(card);
          }
          current = '';
          continue;
        }
      } else if (char === '[') depth++;
      else if (char === ']') depth--;
    }

    if (depth >= 1) current += char;
  }

  if (current.trim()) {
    const card = parseCardObject(current);
    if (card.title) {
      cards.push(card);
    }
  }

  return cards;
}

/**
 * Parse a single card object
 */
function parseCardObject(str: string): AICard {
  const card: AICard = {
    id: generateId(),
    title: '',
  };

  const titleMatch = str.match(/"title"\s*:\s*"([^"]*)"/);
  if (titleMatch) card.title = titleMatch[1];

  const descMatch = str.match(/"description"\s*:\s*"([^"]*)"/);
  if (descMatch) card.description = descMatch[1];

  // Handle both "type" and "cardType" for compatibility
  const typeMatch = str.match(/"(?:card)?[Tt]ype"\s*:\s*"([^"]*)"/);
  if (typeMatch) card.cardType = typeMatch[1] as any;

  const minutesMatch = str.match(/"estimatedMinutes"\s*:\s*(\d+)/);
  if (minutesMatch) card.estimatedMinutes = parseInt(minutesMatch[1]);

  const difficultyMatch = str.match(/"difficulty"\s*:\s*"([^"]*)"/);
  if (difficultyMatch) card.difficulty = difficultyMatch[1] as any;

  const priorityMatch = str.match(/"priority"\s*:\s*"([^"]*)"/);
  if (priorityMatch) card.priority = priorityMatch[1] as any;

  return card;
}

/**
 * Main function to parse streaming content and extract track data
 */
export function parseStreamingTrackData(content: string): ParseResult {
  const result: ParseResult = {
    isTrackCreation: false,
    data: null,
    isComplete: false,
    rawJson: null,
  };

  // Check if this looks like track creation
  if (!detectTrackCreation(content)) {
    return result;
  }

  result.isTrackCreation = true;

  // Extract JSON content
  const jsonContent = extractJsonContent(content);
  if (!jsonContent) {
    return result;
  }

  result.rawJson = jsonContent;

  // Check if JSON is complete
  result.isComplete = content.includes('```json') && content.indexOf('```', content.indexOf('```json') + 7) !== -1;

  // Parse the JSON
  const parsed = parsePartialJson(jsonContent);
  if (!parsed) {
    return result;
  }

  // Extract track data from parsed object
  const trackData = parsed.data || parsed;

  // Determine track type
  const trackType = trackData.trackType || (trackData.maps ? 'hierarchical' : (trackData.cards ? 'linear' : 'hierarchical'));

  result.data = {
    trackName: trackData.trackName || '',
    trackColor: trackData.trackColor || '#6366f1',
    trackIcon: trackData.trackIcon || '🎯',
    trackType: trackType,
    category: trackData.category,
    description: trackData.description,
    maps: trackData.maps || [],
    cards: trackData.cards || [],
  };

  return result;
}

export default parseStreamingTrackData;
